import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  type MRT_ColumnFiltersState,
  type MRT_SortingState,
  type MRT_VisibilityState
} from 'mantine-react-table';
import {
  useTableViews,
  useCreateTableView,
  useUpdateTableView,
  useDeleteTableView,
  useSetDefaultTableView
} from '@/api/common/useTableViews';
import { type TableView, type TableViewConfig } from '@/api/common/tableViews';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { useAdminAuth } from '@/app/ProviderAdminAuth';

export interface SavedViewsConfig {
  tableIdentifier: string
  currentConfig: {
    columnFilters: MRT_ColumnFiltersState
    sorting: MRT_SortingState
    columnVisibility: MRT_VisibilityState
    columnOrder?: string[]
    globalFilter?: string
    pagination?: {
      pageSize: number
      pageIndex: number
    }
  }
  onApplyView: (config: TableViewConfig) => void
  defaultConfig?: TableViewConfig
}

export interface UseSavedViewsReturn {
  views: TableView[]
  personalViews: TableView[]
  tenantViews: TableView[]
  currentView: TableView | null
  isLoading: boolean
  isDirty: boolean

  saveView: (name?: string, scope?: 'personal' | 'tenant') => Promise<void>
  saveAsView: (name: string, scope: 'personal' | 'tenant') => Promise<void>
  createNewView: (name: string, scope: 'personal' | 'tenant') => Promise<void>
  loadView: (viewId: number) => void
  deleteView: (viewId: number) => Promise<void>
  setAsDefault: (viewId: number) => Promise<void>
  clearCurrentView: (clearFilters?: boolean) => void

  isCreating: boolean
  isUpdating: boolean
  isDeleting: boolean
}

export const useSavedViews = ({
  tableIdentifier,
  currentConfig,
  onApplyView,
  defaultConfig
}: SavedViewsConfig): UseSavedViewsReturn => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { tenant } = useRegisterRoute();
  const { adminAuth } = useAdminAuth();
  const [currentView, setCurrentView] = useState<TableView | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [lastLoadedViewId, setLastLoadedViewId] = useState<number | null>(null);

  const { data: views = [], isLoading } = useTableViews(tableIdentifier, tenant?.id);
  const createMutation = useCreateTableView();
  const updateMutation = useUpdateTableView();
  const deleteMutation = useDeleteTableView();
  const setDefaultMutation = useSetDefaultTableView();

  const personalViews = useMemo(
    () => { return views.filter(v => { return v.scope === 'personal'; }); },
    [views]
  );

  const tenantViews = useMemo(
    () => { return views.filter(v => { return v.scope === 'tenant'; }); },
    [views]
  );

  const [hasLoadedInitialView, setHasLoadedInitialView] = useState(false);

  const applyDefaults = useCallback((config: TableViewConfig): TableViewConfig => {
    return {
      columnFilters: config.columnFilters ?? defaultConfig?.columnFilters ?? [],
      sorting: config.sorting ?? defaultConfig?.sorting ?? [],
      columnVisibility: config.columnVisibility ?? defaultConfig?.columnVisibility ?? {},
      columnOrder: config.columnOrder ?? defaultConfig?.columnOrder ?? [],
      globalFilter: config.globalFilter ?? defaultConfig?.globalFilter ?? '',
      pagination: config.pagination ?? defaultConfig?.pagination ?? { pageSize: 10, pageIndex: 0 }
    };
  }, [defaultConfig]);

  const loadView = useCallback((viewId: number) => {
    const view = views.find(v => { return v.id === viewId; });
    if (view) {
      const normalizedViewConfig = applyDefaults(view.viewConfig);

      setLastLoadedViewId(viewId);
      setCurrentView({ ...view, viewConfig: normalizedViewConfig });
      onApplyView(normalizedViewConfig);
      setIsDirty(false);

      setSearchParams(prev => {
        prev.set('view', viewId.toString());
        return prev;
      });
    }
  }, [views, onApplyView, setSearchParams, applyDefaults]);

  const sortObjectKeys = <T,>(obj: Record<string, T>): Record<string, T> => {
    return Object.keys(obj)
      .sort()
      .reduce((acc, key) => {
        acc[key] = obj[key];
        return acc;
      }, {} as Record<string, T>);
  };

  useEffect(() => {
    if (views.length === 0 || hasLoadedInitialView) return;

    const viewIdFromUrl = searchParams.get('view');

    if (viewIdFromUrl) {
      const view = views.find(v => { return v.id === parseInt(viewIdFromUrl); });
      if (view) {
        loadView(view.id);
        setHasLoadedInitialView(true);
      }
      return;
    }

    if (!adminAuth.user?.id) {
      return;
    }

    if (!currentView) {
      const defaultView = views.find(v => {
        return v.isDefault && v.userId === adminAuth.user?.id;
      }) ?? views.find(v => { return v.isDefault; });
      if (defaultView) {
        loadView(defaultView.id);
        setHasLoadedInitialView(true);
      } else {
        setHasLoadedInitialView(true);
      }
    } else {
      setHasLoadedInitialView(true);
    }
  }, [views, adminAuth.user?.id, searchParams, currentView, loadView, hasLoadedInitialView]);

  const normalizeConfig = (config: TableViewConfig): string => {
    const normalized = applyDefaults(config);
    const sortedVisibility = sortObjectKeys(normalized.columnVisibility ?? {});
    const sortableNormalized = {
      ...normalized,
      columnVisibility: sortedVisibility
    };
    const replacer = (_key: string, value: unknown) => {
      if (value instanceof Date) {
        return value.toISOString();
      }
      return value;
    };
    return JSON.stringify(sortableNormalized, replacer);
  };

  useEffect(() => {
    if (!currentView) {
      setIsDirty(false);
      return;
    }

    if (lastLoadedViewId === currentView.id) {
      setLastLoadedViewId(null);
      return;
    }

    const currentNormalized = normalizeConfig(currentConfig);
    const viewNormalized = normalizeConfig(currentView.viewConfig);
    const hasChanged = currentNormalized !== viewNormalized;
    setIsDirty(hasChanged);
  }, [
    currentConfig.columnFilters,
    currentConfig.sorting,
    currentConfig.columnVisibility,
    currentConfig.columnOrder,
    currentConfig.globalFilter,
    currentConfig.pagination,
    currentView,
    lastLoadedViewId
  ]);

  const saveView = useCallback(async (name?: string, scope?: 'personal' | 'tenant') => {
    const viewConfig: TableViewConfig = {
      columnFilters: currentConfig.columnFilters,
      sorting: currentConfig.sorting,
      columnVisibility: currentConfig.columnVisibility,
      columnOrder: currentConfig.columnOrder,
      globalFilter: currentConfig.globalFilter,
      pagination: currentConfig.pagination
    };

    if (currentView && currentView.canEdit) {
      const result = await updateMutation.mutateAsync({
        id: currentView.id,
        params: {
          name: name ?? currentView.name,
          viewConfig
        }
      });
      setCurrentView(result as TableView);
      setIsDirty(false);
    } else if (name) {
      const result = await createMutation.mutateAsync({
        name,
        tableIdentifier,
        viewConfig,
        scope: scope ?? 'personal'
      });
      setCurrentView(result as TableView);
      setIsDirty(false);

      setSearchParams(prev => {
        prev.set('view', (result as TableView).id.toString());
        return prev;
      });
    }
  }, [currentView, currentConfig, tableIdentifier, updateMutation, createMutation, setSearchParams]);

  const saveAsView = useCallback(async (name: string, scope: 'personal' | 'tenant') => {
    const viewConfig: TableViewConfig = {
      columnFilters: currentConfig.columnFilters,
      sorting: currentConfig.sorting,
      columnVisibility: currentConfig.columnVisibility,
      columnOrder: currentConfig.columnOrder,
      globalFilter: currentConfig.globalFilter,
      pagination: currentConfig.pagination
    };

    const result = await createMutation.mutateAsync({
      name,
      tableIdentifier,
      viewConfig,
      scope
    });
    setCurrentView(result as TableView);
    setIsDirty(false);

    setSearchParams(prev => {
      prev.set('view', (result as TableView).id.toString());
      return prev;
    });
  }, [currentConfig, tableIdentifier, createMutation, setSearchParams]);

  const createNewView = useCallback(async (name: string, scope: 'personal' | 'tenant') => {
    const baseDefaultConfig: TableViewConfig = {
      columnFilters: [],
      sorting: [],
      columnVisibility: {},
      columnOrder: [],
      globalFilter: '',
      pagination: { pageSize: 10, pageIndex: 0 }
    };

    const defaultViewConfig = applyDefaults(defaultConfig ?? baseDefaultConfig);

    const result = await createMutation.mutateAsync({
      name,
      tableIdentifier,
      viewConfig: defaultViewConfig,
      scope
    }) as TableView;

    setLastLoadedViewId(result.id);
    setCurrentView(result);
    onApplyView(defaultViewConfig);
    setIsDirty(false);

    setSearchParams(prev => {
      prev.set('view', result.id.toString());
      return prev;
    });
  }, [tableIdentifier, createMutation, setSearchParams, onApplyView, applyDefaults, defaultConfig]);

  const deleteView = useCallback(async (viewId: number) => {
    await deleteMutation.mutateAsync(viewId);

    if (currentView?.id === viewId) {
      setCurrentView(null);
      setSearchParams(prev => {
        prev.delete('view');
        return prev;
      });
    }
  }, [currentView, deleteMutation, setSearchParams]);

  const setAsDefault = useCallback(async (viewId: number) => {
    await setDefaultMutation.mutateAsync(viewId);
  }, [setDefaultMutation]);

  const clearCurrentView = useCallback((clearFilters: boolean = false) => {
    setCurrentView(null);
    setLastLoadedViewId(null);
    setSearchParams(prev => {
      prev.delete('view');
      return prev;
    });

    if (clearFilters) {
      const baseDefaultConfig: TableViewConfig = {
        columnFilters: [],
        sorting: [],
        columnVisibility: {},
        columnOrder: [],
        globalFilter: '',
        pagination: { pageSize: 10, pageIndex: 0 }
      };
      const defaultViewConfig = applyDefaults(defaultConfig ?? baseDefaultConfig);
      onApplyView(defaultViewConfig);
      setIsDirty(false);
    }
  }, [setSearchParams, onApplyView, applyDefaults, defaultConfig]);

  return {
    views,
    personalViews,
    tenantViews,
    currentView,
    isLoading,
    isDirty,

    saveView,
    saveAsView,
    createNewView,
    loadView,
    deleteView,
    setAsDefault,
    clearCurrentView,

    isCreating: createMutation.isLoading,
    isUpdating: updateMutation.isLoading,
    isDeleting: deleteMutation.isLoading,
  };
};
